import type { Locale } from "../../shared/api/types";
import { chromeLocale } from "../../shared/lib/locale-context";

/**
 * Chrome strings of the sprint retrospective page (batch 18 GRF-50, B18-UX-SPECS §9; stream S6). Seeded by stream S0 so
 * the owning stream adds its keys here without touching a shared dictionary. English for every user (ruling
 * 2026-10-04); the Thai column is provisional and unreachable while `chromeLocale()` returns "en", like every other
 * `*Text.ts` dictionary. `{name}` placeholders are filled from `values`.
 */
export type RetrospectiveTextKey =
  | "title"
  | "wentWell"
  | "toImprove"
  | "actions"
  | "backToBacklog"
  | "goal"
  | "dates"
  | "notStarted"
  | "readOnly"
  | "readOnlyOn"
  | "futureSprint"
  | "factsTitle"
  | "committed"
  | "added"
  | "removed"
  | "completed"
  | "notDone"
  | "carriedTo"
  | "theBacklog"
  | "itemsPoints"
  | "itemPoints"
  | "itemsPoint"
  | "itemPoint"
  | "withoutPoints"
  | "factsLoading"
  | "copy"
  | "copied"
  | "copyFailed"
  | "addLabel"
  | "addHint"
  | "add"
  | "adding"
  | "empty"
  | "edit"
  | "remove"
  | "save"
  | "cancel"
  | "edited"
  | "previousText"
  | "removedBy"
  | "removeConfirm"
  | "conflict"
  | "yourText"
  | "makeAction"
  | "actionTitle"
  | "summary"
  | "owner"
  | "chooseOwner"
  | "dueDate"
  | "create"
  | "creating"
  | "needSummary"
  | "needOwner"
  | "ownedBy"
  | "due"
  | "loading"
  | "notAvailable"
  | "notAvailableReason"
  | "tooLong"
  | "needText"
  | "retrospective"
  | "writeRetro"
  | "fromRetro";

const RETROSPECTIVE_TEXT: Record<Locale, Record<RetrospectiveTextKey, string>> = {
  en: {
    title: "{sprint} retrospective",
    wentWell: "Went well",
    toImprove: "To improve",
    actions: "Actions",
    backToBacklog: "Back to the backlog",
    goal: "Goal: {goal}",
    dates: "{start} – {end}",
    notStarted: "Not started yet",
    readOnly: "This retrospective is read-only: the sprint was completed more than 30 days ago. Action items can still be made.",
    readOnlyOn: "Editable until {date}.",
    futureSprint: "This sprint has not started. Its retrospective opens once it starts.",
    factsTitle: "What the sprint delivered",
    committed: "Committed",
    added: "Added",
    removed: "Removed",
    completed: "Completed",
    notDone: "Not done",
    carriedTo: "carried to {target}",
    theBacklog: "the backlog",
    itemsPoints: "{items} items · {points} points",
    itemPoints: "{items} item · {points} points",
    itemsPoint: "{items} items · {points} point",
    itemPoint: "{items} item · {points} point",
    withoutPoints: "{count} of {total} items have no story points and are not counted in points.",
    factsLoading: "Sprint figures are not available.",
    copy: "Copy as text",
    copied: "Copied the retrospective as text.",
    copyFailed: "Could not copy. Select the text and copy it yourself.",
    addLabel: "Add to {list}",
    addHint: "Enter adds; Shift+Enter starts a new line.",
    add: "Add",
    adding: "Adding…",
    empty: "Nothing yet.",
    edit: "Edit",
    remove: "Remove",
    save: "Save",
    cancel: "Cancel",
    edited: "edited",
    previousText: "Earlier text",
    removedBy: "Entry removed by {name}.",
    removeConfirm: "Remove this entry? It shows as removed for everyone.",
    conflict: "Someone changed this entry. Their text is shown; yours is kept below to copy.",
    yourText: "Your text",
    makeAction: "Make it an action item",
    actionTitle: "Make an action item",
    summary: "Summary",
    owner: "Owner",
    chooseOwner: "Choose a person",
    dueDate: "Due date",
    create: "Create action item",
    creating: "Creating…",
    needSummary: "Write a summary for the action item.",
    needOwner: "Choose who owns the action.",
    ownedBy: "Owner: {name}",
    due: "Due {date}",
    loading: "Loading…",
    notAvailable: "Not available",
    notAvailableReason: "The sprint retrospective is for internal project members.",
    tooLong: "Keep it to 500 characters.",
    needText: "Write something first.",
    retrospective: "Retrospective",
    writeRetro: "Write the retrospective",
    fromRetro: "From retrospective: {sprint}",
  },
  th: {
    title: "การทบทวนหลังสปรินต์ {sprint}",
    wentWell: "สิ่งที่ทำได้ดี",
    toImprove: "สิ่งที่ควรปรับปรุง",
    actions: "สิ่งที่จะทำ",
    backToBacklog: "กลับไปที่แบ็กล็อก",
    goal: "เป้าหมาย: {goal}",
    dates: "{start} – {end}",
    notStarted: "ยังไม่เริ่ม",
    readOnly: "การทบทวนนี้อ่านได้อย่างเดียว เพราะสปรินต์ปิดไปแล้วเกิน 30 วัน แต่ยังสร้างงาน Action ได้",
    readOnlyOn: "แก้ไขได้ถึง {date}",
    futureSprint: "สปรินต์นี้ยังไม่เริ่ม การทบทวนจะเปิดเมื่อเริ่มสปรินต์",
    factsTitle: "ผลงานของสปรินต์",
    committed: "ตั้งใจทำ",
    added: "เพิ่มระหว่างทาง",
    removed: "นำออก",
    completed: "เสร็จแล้ว",
    notDone: "ยังไม่เสร็จ",
    carriedTo: "ย้ายไป {target}",
    theBacklog: "แบ็กล็อก",
    itemsPoints: "{items} รายการ · {points} พอยต์",
    itemPoints: "{items} รายการ · {points} พอยต์",
    itemsPoint: "{items} รายการ · {points} พอยต์",
    itemPoint: "{items} รายการ · {points} พอยต์",
    withoutPoints: "{count} จาก {total} รายการไม่มีสตอรีพอยต์ จึงไม่นับในพอยต์",
    factsLoading: "ไม่มีตัวเลขของสปรินต์",
    copy: "คัดลอกเป็นข้อความ",
    copied: "คัดลอกการทบทวนเป็นข้อความแล้ว",
    copyFailed: "คัดลอกไม่ได้ กรุณาเลือกข้อความแล้วคัดลอกเอง",
    addLabel: "เพิ่มใน {list}",
    addHint: "กด Enter เพื่อเพิ่ม กด Shift+Enter เพื่อขึ้นบรรทัดใหม่",
    add: "เพิ่ม",
    adding: "กำลังเพิ่ม…",
    empty: "ยังไม่มี",
    edit: "แก้ไข",
    remove: "ลบ",
    save: "บันทึก",
    cancel: "ยกเลิก",
    edited: "แก้ไขแล้ว",
    previousText: "ข้อความก่อนหน้า",
    removedBy: "รายการนี้ถูกลบโดย {name}",
    removeConfirm: "ลบรายการนี้หรือไม่ ทุกคนจะเห็นว่าถูกลบ",
    conflict: "มีคนแก้ไขรายการนี้แล้ว ข้อความของเขาแสดงอยู่ ข้อความของคุณเก็บไว้ด้านล่างให้คัดลอก",
    yourText: "ข้อความของคุณ",
    makeAction: "สร้างเป็นงาน Action",
    actionTitle: "สร้างงาน Action",
    summary: "สรุป",
    owner: "ผู้รับผิดชอบ",
    chooseOwner: "เลือกคน",
    dueDate: "วันครบกำหนด",
    create: "สร้างงาน Action",
    creating: "กำลังสร้าง…",
    needSummary: "กรุณาเขียนสรุปของงาน Action",
    needOwner: "กรุณาเลือกผู้รับผิดชอบ",
    ownedBy: "ผู้รับผิดชอบ: {name}",
    due: "ครบกำหนด {date}",
    loading: "กำลังโหลด…",
    notAvailable: "ไม่พร้อมใช้งาน",
    notAvailableReason: "การทบทวนสปรินต์มีไว้สำหรับสมาชิกภายในโครงการ",
    tooLong: "ไม่เกิน 500 ตัวอักษร",
    needText: "กรุณาเขียนข้อความก่อน",
    retrospective: "การทบทวน",
    writeRetro: "เขียนการทบทวน",
    fromRetro: "มาจากการทบทวน: {sprint}",
  },
};

/** One chrome string, `{name}` placeholders filled from `values`. */
export function retrospectiveText(key: RetrospectiveTextKey, values: Record<string, string | number> = {}, locale?: Locale): string {
  const template = RETROSPECTIVE_TEXT[chromeLocale(locale)][key];
  return template.replace(/\{([a-z]+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}
